// Lib
import React from 'react';
import { useSelector } from 'react-redux';
import { LoadingIndicator } from '@milanote/roma';

// Utils
import { getFileSizeComparisonParts } from '../../../common/files/fileSizeUtils';
import { useTranslator } from '../../components/i18n/useTranslator';
import { userLocalePreferenceSelector } from '../../user/currentUserSelector';

// Selectors
import { fileTransferToastSelector, type FileTransferToastSelectorProps } from './fileTransferToastSelector';

// Hooks
import useStalledBoolean from '../../utils/react/useStalledBoolean';

// Components
import Toast from '../../structural/toast/Toast';
import Spinner from '../../components/loaders/Spinner';
import ProgressBar from '../file/uploader/ProgressBar';
import AnimationLock from '../../components/animations/AnimationLock';

// Constants
import { ELEMENT_ATTACHMENT_ACTION_TYPES } from '../../../common/elements/elementConstants';

// Types
import type { ReduxState } from '../../types/reduxTypes';

// Styles
import './FileTransferToast.scss';

const ProgressSize: React.FC<{ loaded: number; total: number }> = ({ loaded, total }) => {
    const commonTranslate = useTranslator('common');
    const locale = useSelector(userLocalePreferenceSelector);
    const { suffix, totalFormatted, partialFormatted } = getFileSizeComparisonParts(
        loaded,
        total,
        false,
        commonTranslate,
        locale,
    );
    return (
        <div className="progress-size">
            <span className="partial">{partialFormatted}</span>
            <span className="slash">/</span>
            <span className="total">{totalFormatted}</span>
            <span className="unit"> {suffix}</span>
        </div>
    );
};

/**
 * Owns-motion wiring supplied by a host that drives the toast's enter/exit itself (the image modal's
 * bottom-surface handoff). When present, the toast renders through its exit under the host's control
 * rather than self-unmounting the moment the transfer ends.
 */
export type FileTransferToastMotion = {
    /** Motion state classes (is-shown / has-entered / is-exiting-below) from the host's lifecycle. */
    className: string;
    onTransitionEnd: (event: React.TransitionEvent<HTMLDivElement>) => void;
};

// Structured as a subcomponent of the Toast to ensure it updates when the store changes
type FileTransferToastProps = FileTransferToastSelectorProps & {
    groupId?: string;
    uploadLoadingLabel?: string;
    motion?: FileTransferToastMotion;
};

const useFileTransferToastState = ({ elementId, excludedElementIds }: FileTransferToastSelectorProps) =>
    useSelector((state: ReduxState) => fileTransferToastSelector(state, { elementId, excludedElementIds }));

const FileTransferToastContent: React.FC<FileTransferToastSelectorProps & { uploadLoadingLabel?: string }> = ({
    elementId,
    excludedElementIds,
    uploadLoadingLabel,
}) => {
    const { isTransferringAttachmentWithProgress, progress } = useFileTransferToastState({
        elementId,
        excludedElementIds,
    });
    const translate = useTranslator('canvas', 'toast.fileTransfer');

    const { count, total, loaded, showProgress, actionType } = progress;

    const isDownloading = actionType === ELEMENT_ATTACHMENT_ACTION_TYPES.DOWNLOAD;
    const textMessage = isDownloading ? translate('downloading') : translate('uploading');
    const showUploadLoadingIndicator = !isDownloading && !!uploadLoadingLabel;
    const showProgressBar = isDownloading || showProgress;
    const percentageComplete = total > 0 ? (loaded / total) * 100 : 0;

    const isSingleFile = count === 1;
    const singleFileText = translate('file_one');
    const multipleFilesText = translate('file_other');
    const fileText = isSingleFile ? singleFileText : multipleFilesText;

    // Don't update the toast contents if the component is getting removed
    return (
        <AnimationLock animating={!isTransferringAttachmentWithProgress}>
            {showUploadLoadingIndicator ? (
                <LoadingIndicator size="xs">{uploadLoadingLabel}</LoadingIndicator>
            ) : (
                <>
                    {showProgressBar ? <ProgressBar percentageComplete={percentageComplete} /> : <Spinner show />}
                    <span className="uploading-message">
                        {textMessage} {count} {fileText}&nbsp;
                    </span>
                    {showProgress && !isDownloading && <ProgressSize loaded={loaded} total={total} />}
                </>
            )}
        </AnimationLock>
    );
};

const FileTransferToast: React.FC<FileTransferToastProps> = ({
    elementId,
    excludedElementIds,
    groupId,
    uploadLoadingLabel,
    motion,
}) => {
    const { isTransferringAttachmentWithProgress } = useFileTransferToastState({ elementId, excludedElementIds });
    // Ensure the toast shows for 1 second after the upload completes - so that we can show the
    //  "completed" message
    const showToast = useStalledBoolean(isTransferringAttachmentWithProgress, 0, 1000);

    // In owns-motion mode the host controls mounting and drives the exit, so the toast must stay rendered
    // until the host unmounts it; only the self-managed (workspace) path unmounts on the stalled boolean.
    if (!motion && !showToast) return null;

    return (
        <Toast
            className={`FileTransferToast${motion ? ` ${motion.className}` : ''}`}
            groupId={groupId}
            ownsMotion={!!motion}
            onTransitionEnd={motion?.onTransitionEnd}
        >
            <FileTransferToastContent
                elementId={elementId}
                excludedElementIds={excludedElementIds}
                uploadLoadingLabel={uploadLoadingLabel}
            />
        </Toast>
    );
};

export default FileTransferToast;
